import {
  SkillDetail,
  SkillMetadata,
  SkillVisibility,
  SkillVisibilitySchema,
  SkillWritePayload,
} from "@models";
import {
  Business,
  Groups,
  Lock,
  Public,
  type SvgIconComponent,
} from "@mui/icons-material";

export const skillVisibilityLabel: Record<SkillVisibility, string> = {
  PRIVATE: "Private",
  ORGANIZATION: "Organization",
  GLOBAL: "Global",
  FORMLY_INTERNAL: "Formly internal",
};

export const skillVisibilityIconByVisibility: Record<
  SkillVisibility,
  SvgIconComponent
> = {
  PRIVATE: Lock,
  ORGANIZATION: Business,
  GLOBAL: Public,
  FORMLY_INTERNAL: Groups,
};

export const skillVisibilityChipColorByVisibility: Record<
  SkillVisibility,
  "default" | "primary" | "secondary" | "info"
> = {
  PRIVATE: "default",
  ORGANIZATION: "info",
  GLOBAL: "secondary",
  FORMLY_INTERNAL: "primary",
};

export const SKILL_NAME_KEBAB_CASE_ERROR =
  "Name must use lowercase letters, numbers, and single hyphens, for example reviewer-voice.";
export const SKILL_NAME_KEBAB_CASE_HELPER_TEXT =
  "Use lowercase letters, numbers, and single hyphens, for example reviewer-voice.";

export const isSkillNameKebabCase = (name: string) =>
  name.length <= 64 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name);

const skillVisibilityValues = SkillVisibilitySchema.describe().oneOf.filter(
  (value): value is SkillVisibility =>
    typeof value === "string" && SkillVisibilitySchema.isValidSync(value)
);

export const isSkillVisibility = (value: unknown): value is SkillVisibility =>
  skillVisibilityValues.some((visibility) => visibility === value);

type SkillVisibilityOption = {
  value: SkillVisibility;
  label: string;
  description: string;
  disabled: boolean;
};

export const getSkillVisibilityOptions = ({
  canCreateGlobal,
  canCreateFormlyInternal,
  includeUnavailable = false,
}: {
  canCreateGlobal: boolean;
  canCreateFormlyInternal: boolean;
  includeUnavailable?: boolean;
}): SkillVisibilityOption[] =>
  skillVisibilityValues
    .map((value) => {
      const disabled =
        (value === "GLOBAL" && !canCreateGlobal) ||
        (value === "FORMLY_INTERNAL" && !canCreateFormlyInternal);

      return {
        value,
        label: skillVisibilityLabel[value],
        description: {
          PRIVATE: "Only you can use it",
          ORGANIZATION: "Everyone in your org can use it",
          GLOBAL: "All users can use it",
          FORMLY_INTERNAL: "Admins with write access can use it",
        }[value],
        disabled,
      };
    })
    .filter((option) => includeUnavailable || !option.disabled);

export const getSkillAuthorName = (skill: SkillMetadata | SkillDetail) => {
  const user = skill.createdByUser;
  if (!user) return null;

  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email
  );
};

type PermissionBearingUser = {
  permissions?: Array<string | undefined>;
  email?: string | null;
};

export const isAdminUser = (user?: PermissionBearingUser | null) => {
  return !!user?.permissions?.includes("write:configuration");
};

export const canCreateInternalSkill = (user?: PermissionBearingUser | null) => {
  return isAdminUser(user);
};

type SkillMarkdownUploadValues = Omit<SkillWritePayload, "forceEnabled">;

const getSkillMarkdownUploadErrorMessage = (caught: unknown) =>
  caught instanceof Error ? caught.message : "Could not read the file.";

const parseFrontmatterValue = (value: string) => {
  const trimmed = value.trim();

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      return JSON.parse(trimmed) as string;
    } catch {
      return trimmed.slice(1, -1);
    }
  }

  if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
    return trimmed.slice(1, -1).replace(/''/g, "'");
  }

  return trimmed;
};

const parseFrontmatterBlock = (frontmatter: string) =>
  Object.fromEntries(
    frontmatter
      .split(/\r?\n/)
      .map((line) => line.match(/^([A-Za-z][A-Za-z0-9_-]*):\s*(.*)$/))
      .filter((match): match is RegExpMatchArray => Boolean(match))
      .map((match) => [match[1], parseFrontmatterValue(match[2])])
  );

const splitMarkdownFrontmatter = (content: string) => {
  const match = content.match(
    /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/
  );

  if (!match) {
    return { frontmatter: {}, body: content };
  }

  return {
    frontmatter: parseFrontmatterBlock(match[1]),
    body: content.slice(match[0].length).replace(/^\r?\n/, ""),
  };
};

const extractDescriptionFromMarkdown = (content: string) => {
  return (
    splitMarkdownFrontmatter(content)
      .body.split(/\n\s*\n/)
      .map((part) => part.trim())
      .find((part) => part && !part.startsWith("#")) ?? ""
  );
};

const readSkillMarkdownFile = async (file: File) => {
  if (!file.name.toLowerCase().endsWith(".md")) {
    throw new Error("Skill uploads must be markdown files.");
  }

  const { frontmatter, body } = splitMarkdownFrontmatter(await file.text());
  const frontmatterVisibility =
    typeof frontmatter.visibility === "string" &&
    isSkillVisibility(frontmatter.visibility)
      ? frontmatter.visibility
      : undefined;

  return {
    name:
      (typeof frontmatter.name === "string" ? frontmatter.name.trim() : "") ||
      file.name.replace(/\.md$/i, ""),
    descriptionMd:
      (typeof frontmatter.description === "string"
        ? frontmatter.description.trim()
        : "") || extractDescriptionFromMarkdown(body),
    contentMd: body,
    ...(frontmatterVisibility ? { visibility: frontmatterVisibility } : {}),
  };
};

export const handleSkillMarkdownUpload = async ({
  file,
  input,
  onDraft,
  onError,
}: {
  file?: File;
  input?: HTMLInputElement | null;
  onDraft: (values: SkillMarkdownUploadValues) => void;
  onError: (message: string) => void;
}) => {
  if (!file) return;

  try {
    const draft = await readSkillMarkdownFile(file);
    onDraft({
      ...draft,
      visibility: draft.visibility ?? "PRIVATE",
    });
  } catch (caught) {
    onError(getSkillMarkdownUploadErrorMessage(caught));
  } finally {
    if (input) {
      input.value = "";
    }
  }
};

export const formatSkillDate = (value?: Date | string) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
